import { create } from 'zustand';
import { Conversation, IConversationItem } from './conversationTypes';
import { toast } from 'react-toastify';
import { conversationApi } from '../api';
import { ITavusPersona } from 'entities/tavus/persona';
import { downloadFile, getErrorMessageV2, logger } from 'shared/lib';
import { ITMessageDaily } from 'shared/model';
import { useMorph } from 'features/recognize-emotions';

type State = {
  currentConversation: Conversation | null;
  conversationStarted: boolean;
  downloadVideoLoading: boolean;
  conversationLoading: boolean;
  callIsFinishing: boolean;
  conversationsListLoading: boolean;
  conversationsList: IConversationItem[];
  transcriptionsLoading: boolean;
  viewTranscriptionsModal: IConversationItem | null;
  viewFormDataModal: IConversationItem | null;
};

type Action = {
  startConversation: (data: {
    currentPersona: ITavusPersona | null;
    key: string | undefined;
    language: string | undefined;
    preconversationId: string | null | undefined;
    data?: {
      [key: string]: string;
    };
  }) => Promise<Conversation>;
  resetConversationFlags: () => void;
  endConversation: (messages: ITMessageDaily[], key?: string) => Promise<void>;
  downloadVideo: (conversationId: string) => Promise<void>;
  getStreamLinkVideo: (
    conversationId: string
  ) => Promise<string | undefined | null>;
  fetchAll: (personaId: string) => Promise<void>;
  getTranscriptions: (conversationId: string) => Promise<void>;
  setViewTranscriptionsModal: (conversation: IConversationItem | null) => void;
  setViewFormDataModal: (conversation: IConversationItem | null) => void;
  setStartStreamTime: (
    conversationId: string,
    startTime: string,
    key?: string
  ) => Promise<void>;
};

type Store = State & Action;

export const conversationStore = create<Store>((set, get) => ({
  currentConversation: null,
  conversationLoading: false,
  conversationStarted: false,
  downloadVideoLoading: false,
  conversationsListLoading: false,
  conversationsList: [],
  transcriptionsLoading: false,
  viewTranscriptionsModal: null,
  viewFormDataModal: null,
  startConversation: async ({
    currentPersona,
    key,
    language,
    preconversationId,
    data
  }) => {
    if (!currentPersona) {
      throw new Error('No persona selected');
    }
    set({ conversationLoading: true });
    try {
      const conversation = await conversationApi.createConversation({
        personaId: currentPersona.id!,
        key,
        language,
        preconversationId,
        data
      });
      set({ currentConversation: conversation });
      return conversation;
    } finally {
      set({ conversationLoading: false });
    }
  },
  resetConversationFlags: () => {
    set({
      conversationStarted: false,
      currentConversation: null
    });
  },
  getStreamLinkVideo: async (conversationId: string) => {
    try {
      const data = await conversationApi.downloadVideo(conversationId);
      return data?.stream_url;
    } catch (error) {
      toast.error(getErrorMessageV2(error, 'Please try again later.'));
    } finally {
      set({ downloadVideoLoading: false });
    }
  },
  downloadVideo: async (conversationId) => {
    set({ downloadVideoLoading: true });
    try {
      const data = await conversationApi.downloadVideo(conversationId);
      if (data.download_url) {
        downloadFile(data.download_url);
      }
    } catch (error) {
      toast.error(getErrorMessageV2(error, 'Please try again later.'));
    } finally {
      set({ downloadVideoLoading: false });
    }
  },
  callIsFinishing: false,
  endConversation: async (messages, key) => {
    if (get().callIsFinishing) {
      logger.error('Last end conversation request is not finished yet.');
      return;
    }

    const timeline = [...useMorph.getState().timeline];
    const emotionalTimeline = timeline.length ? timeline.join('\n') : '';

    useMorph.getState().clearTimeline();
    logger.debug('timeline', timeline);
    set({ callIsFinishing: true });
    const conversationId = get().currentConversation?.conversation_id;
    set({
      currentConversation: null
    });
    if (!conversationId) {
      toast.error('No conversation to end.');
      return;
    }

    try {
      await conversationApi.endConversation(
        conversationId,
        messages,
        emotionalTimeline,
        key
      );
      logger.debug('call end request');
    } catch (error) {
      toast.error("Couldn't end the conversation. Please try again later.");
    } finally {
      set({ callIsFinishing: false });
    }
  },
  fetchAll: async (personaId) => {
    set({ conversationsListLoading: true });
    try {
      const data = await conversationApi.fetchAll(personaId);
      set({
        conversationsList: data
      });
    } catch (error) {
      toast.error(
        "Couldn't retrieve the list of conversations. Please try again later."
      );
    } finally {
      set({ conversationsListLoading: false });
    }
  },
  getTranscriptions: async (conversationId) => {
    set({
      transcriptionsLoading: true
    });
    try {
      const data = await conversationApi.getTranscriptions(conversationId);
      set((state) => ({
        conversationsList: state.conversationsList.map((item) => {
          if (item.id === conversationId) {
            return {
              ...item,
              external_transcripts: data
            };
          }
          return item;
        })
      }));
    } catch (error) {
      toast.error(
        "Couldn't retrieve the list of transcriptions. Please try again later."
      );
    } finally {
      set({
        transcriptionsLoading: false
      });
    }
  },
  setViewTranscriptionsModal: (conversation) => {
    set({ viewTranscriptionsModal: conversation });
  },
  setViewFormDataModal: (conversation) => {
    set({ viewFormDataModal: conversation });
  },
  setStartStreamTime: async (conversationId, startTime, key) => {
    try {
      await conversationApi.startTimerOnce(conversationId, startTime, key);
    } catch (error) {
      toast.error(
        "Couldn't set the start stream time. Please try again later."
      );
    } finally {
      set({ downloadVideoLoading: false });
    }
  }
}));
